import { Tag, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { type SubcontractorReadiness } from "../types/types.ts"

/**
 * Chen's F1: *"allow creation with basic details, show 'Setup incomplete'"*.
 *
 * Reporting only. The 2026-10-02 decision was that nothing is blocked by an
 * incomplete profile — not project assignment, not a contract, not a payment —
 * so this never gates a control. It tells a GC what is outstanding and leaves
 * the judgement to them.
 *
 * Renders nothing at all when the record is complete, and equally when the
 * server sent no `readiness` at all: a client newer than its API should stay
 * quiet rather than claim everything is missing.
 */

interface Props {
  readiness?: SubcontractorReadiness
  /** The list wants a bare count; the profile names what is missing. */
  compact?: boolean
}

export const SetupIncompleteBadge = ({ readiness, compact = false }: Props) => {
  const { t } = useTranslation()

  if (!readiness || readiness.complete || readiness.missing.length === 0) {
    return null
  }

  const labels = readiness.missing.map((gap) => gap.label)
  const summary = labels.join(", ")

  return (
    <Tooltip title={summary}>
      <Tag color={"warning"} data-testid={"setup-incomplete-badge"}>
        {compact
          ? t("subcontractorReadiness.incompleteCount", {
              count: labels.length
            })
          : t("subcontractorReadiness.incompleteWithList", { list: summary })}
      </Tag>
    </Tooltip>
  )
}
